<template>
    <div class="grid">
        <div class="unit whole">
            <template v-if="pupil">
                <h1 class="dd-privacy-mask">{{ lang.get('Hello %s', pupil.name) }}</h1>
            </template>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="grid">
                <div class="unit half">
                    <h2 style="margin-top: 0"><i class="fa fa-book-open"></i> {{ lang.get('Your book') }}</h2>
                    <div class="panel">
                        <div class="grid">
                            <template v-if="book">
                                <div class="unit half book">
                                    <RouterLink to="/pupil/read-book">
                                        <BookCover :cover-url="book.url_cover" :book="book" />
                                    </RouterLink>
                                </div>
                                <div class="unit half book">
                                    <h3 :class="{ r2l: book.backwards }">{{ book.title }}</h3>
                                    <p class="book-info">{{ book.author }}</p>
                                    <template v-if="book.pupil_overview">
                                        <div
                                            ref="pupilOverview"
                                            :class="showFullOverview ? 'pupil_overview_full' : 'pupil_overview_short'"
                                        >
                                            <p>{{ book.pupil_overview }}</p>
                                        </div>
                                        <div
                                            v-if="overviewOverflows"
                                            class="show_more"
                                            @click="showFullOverview = !showFullOverview"
                                        >
                                            {{ showFullOverview ? lang.get('Show less') : lang.get('Show all') }}
                                        </div>
                                    </template>
                                    <RouterLink v-if="book.show_as_epub" class="btn" to="/pupil/read-book/epub">{{
                                        lang.get('Read book')
                                    }}</RouterLink>
                                    <RouterLink v-else class="btn" to="/pupil/read-book">{{
                                        lang.get('Read book')
                                    }}</RouterLink>
                                </div>
                            </template>
                            <p
                                v-else
                                v-router-link
                                v-html="
                                    lang.get(
                                        `You don't have a book at the moment, please visit the %slibrary%s to choose one.`,
                                        '<a href=/pupil/change-book>',
                                        '</a>',
                                    )
                                "
                            ></p>
                        </div>
                    </div>
                    <RouterLink class="btn" to="/pupil/change-book">{{ lang.get('Visit library') }}</RouterLink>
                </div>
                <div class="unit half">
                    <h2 style="margin-top: 0"><i class="fa fa-clipboard-question"></i> {{ lang.get('Your tasks') }}</h2>
                    <PupilCurrentTasks :pupil="pupil" :book="book" :tasks="currentBookTasks" />
                </div>
            </div>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="grid">
                <div v-if="otherBooksTasks.length > 0" class="unit half">
                    <h2><i class="fa fa-clipboard-question"></i> {{ lang.get('Other tasks') }}</h2>
                    <PupilOtherTasks :tasks="otherBooksTasks" />
                </div>
                <div v-if="currentBookCompletedTasks.length + otherBookCompletedTasks.length != 0" class="unit half">
                    <h2 style=""><i class="fa fa-clipboard-check"></i> {{ lang.get('Completed tasks') }}</h2>
                    <PupilCompletedTasks
                        :currentBookCompletedTasks="currentBookCompletedTasks"
                        :otherBookCompletedTasks="otherBookCompletedTasks"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { PupilTaskOverview } from '@ilteducation/giglets-types';

const store = usePupilStore();
store.fetchPupil();
store.fetchTasks();
store.fetchBook();
const { pupil, tasks, book } = storeToRefs(store);
const lang = useLang();
const { getImageUrl } = useImageCacheReset();
const { changeBackground } = useBackground();
definePage({ meta: { area: 'home' } });
const showFullOverview = ref(false);

const currentBookTasks = computed<Array<PupilTaskOverview>>(() => {
    return tasks.value.filter((t) => t.task.book.id === book.value?.id && t.status === 1);
});

const otherBooksTasks = computed<Array<PupilTaskOverview>>(() => {
    return tasks.value.filter((t) => t.task.book.id !== book.value?.id && t.status === 1);
});

const completedTasks = computed<Array<PupilTaskOverview>>(() => {
    return tasks.value
        .filter((t) => t.status >= 2 && t.status <= 5)
        .filter((t) => {
            return t.finished && DateTime.fromISO(t.finished).diffNow('months').months >= -3;
        });
});

const currentBookCompletedTasks = computed(() => {
    return completedTasks.value.filter((t) => t.task.book.id === book.value?.id);
});

const otherBookCompletedTasks = computed(() => {
    return completedTasks.value.filter((t) => t.task.book.id !== book.value?.id);
});

const pupilOverview = ref<HTMLDivElement | null>(null);
const overviewOverflows = ref(false);

const unwatch = watch(
    () => pupilOverview.value,
    async (po) => {
        if (po) {
            unwatch();
            await nextTick();
            overviewOverflows.value = po.scrollHeight > po.clientHeight;
        }
    },
);

watchEffect(() => {
    if (book.value?.url_bg) {
        const bgUrl = getImageUrl(book.value.url_bg, book.value);
        changeBackground(bgUrl);
    }
});
</script>
<style scoped lang="scss">
.pupil_overview_short {
    max-height: 160px;
    overflow: hidden;
    position: relative;

    &::before {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        background: linear-gradient(transparent 100px, rgba(250, 250, 250, 0.9));
    }
}

.pupil_overview_full {
    p {
        margin-bottom: 5px;
    }
}

.show_more {
    padding: 10px 0px 20px 0px;
    text-align: right;
    cursor: pointer;
    font-family: HeinemannSpecial-Bold;
    font-size: 0.8em;
}

body.od .show_more {
    font-family: open-dyslexic-bold;
}
</style>
